import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AdminSidebar, SkeletonTable, EmptyState, PageHeader } from "@/components/AdminLayout";
import api from "@/utils/api";

export default function WorkerManagement() {
  const [workers, setWorkers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPinsModal, setShowPinsModal] = useState(false);
  const [uploadedWorkers, setUploadedWorkers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    pin: "",
    employee_id: "",
    department: "",
    designation: "",
    preferred_language: "English",
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    try {
      const response = await api.get("/users?role=worker");
      setWorkers(response.data.filter(u => u.role === "worker"));
    } catch (error) {
      toast.error("Failed to load workers");
    } finally {
      setLoading(false);
    }
  };

  const handleAddWorker = async () => {
    try {
      await api.post("/users", {
        ...formData,
        role: "worker",
      });
      toast.success("Worker added successfully");
      setShowAddModal(false);
      setFormData({
        full_name: "",
        phone: "",
        pin: "",
        employee_id: "",
        department: "",
        designation: "",
        preferred_language: "English",
      });
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add worker");
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/workers/bulk-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success(`Successfully created ${response.data.count} workers!`);
      
      // Store workers with PINs for download
      if (response.data.workers && response.data.workers.length > 0) {
        setUploadedWorkers(response.data.workers);
        setShowPinsModal(true);
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn("CSV import errors:", response.data.errors);
      }
      
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk upload failed");
    } finally {
      e.target.value = ''; // Reset file input
    }
  };

  const downloadPins = () => {
    // Create CSV content
    const csvHeader = "Name,Phone,Employee ID,PIN\n";
    const csvRows = uploadedWorkers.map(w => 
      `${w.name},${w.phone},${w.employee_id},${w.pin}`
    ).join('\n');
    const csvContent = csvHeader + csvRows;
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `worker_pins_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success("PINs downloaded successfully!");
  };

  if (loading) {
    return (
      <div className="flex">
        <AdminSidebar active="workers" />
        <div className="admin-content">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="h-10 bg-slate-200 rounded w-64 mb-2 animate-pulse"></div>
              <div className="h-5 bg-slate-200 rounded w-48 animate-pulse"></div>
            </div>
          </div>
          <SkeletonTable rows={5} />
        </div>
      </div>
    );
  }

  // Filter workers
  const filteredWorkers = workers.filter(worker => {
    const matchesSearch = !searchTerm || 
      worker.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      worker.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      worker.phone?.includes(searchTerm);
    const matchesDept = !filterDept || worker.department === filterDept;
    return matchesSearch && matchesDept;
  });

  const departments = [...new Set(workers.map(w => w.department).filter(Boolean))];

  return (
    <div className="flex">
      <AdminSidebar active="workers" />
      <div className="admin-content">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900" data-testid="workers-title">Worker Management</h1>
            <p className="text-slate-600 mt-2">{workers.length} workers registered</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              onClick={() => document.getElementById('csv-upload').click()}
              variant="outline"
              className="border-[#2e7fd4] text-[#2e7fd4] hover:bg-[#2e7fd4] hover:text-white"
              data-testid="bulk-upload-button"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Bulk Upload CSV
            </Button>
            <input
              id="csv-upload"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleBulkUpload}
            />
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#0a2240] hover:bg-[#0f325c] text-white"
              data-testid="add-worker-button"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Worker
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Search by name, ID, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-11"
              data-testid="search-workers"
            />
          </div>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            className="h-11 px-4 border border-slate-200 rounded-md bg-white min-w-[200px]"
            data-testid="filter-department"
          >
            <option value="">All Departments</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>

        {/* Workers Table */}
        {filteredWorkers.length === 0 ? (
          <EmptyState
            title="No Workers Found"
            description={searchTerm || filterDept ? "Try adjusting your search or filters" : "Add your first worker to get started"}
            action={
              <Button onClick={() => setShowAddModal(true)} className="bg-[#0a2240] hover:bg-[#0f325c] text-white">
                Add Worker
              </Button>
            }
          />
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto responsive-table">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Employee ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Department</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Language</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredWorkers.map((worker) => (
                    <tr key={worker.id} className="hover:bg-slate-50" data-testid="worker-row">
                      <td className="px-6 py-4">
                        <p className="font-semibold text-slate-900">{worker.full_name}</p>
                        <p className="text-sm text-slate-500">{worker.designation}</p>
                      </td>
                      <td className="px-6 py-4 text-slate-600 font-mono">{worker.employee_id}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-slate-100 rounded text-sm text-slate-700">
                          {worker.department}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{worker.phone}</td>
                      <td className="px-6 py-4 text-slate-600">{worker.preferred_language}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add Worker Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Add New Worker</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="pin">4-Digit PIN</Label>
                  <Input
                    id="pin"
                    type="password"
                    maxLength={4}
                    value={formData.pin}
                    onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="employee_id">Employee ID</Label>
                  <Input
                    id="employee_id"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="designation">Designation</Label>
                  <Input
                    id="designation"
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="language">Preferred Language</Label>
                  <select
                    id="language"
                    value={formData.preferred_language}
                    onChange={(e) => setFormData({ ...formData, preferred_language: e.target.value })}
                    className="w-full h-10 px-3 border border-slate-200 rounded-md"
                  >
                    <option value="Hindi">Hindi</option>
                    <option value="Marathi">Marathi</option>
                    <option value="Gujarati">Gujarati</option>
                    <option value="English">English</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setShowAddModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddWorker}
                  className="flex-1 bg-[#0a2240] hover:bg-[#0f325c] text-white"
                >
                  Add Worker
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* PINs Modal */}
        {showPinsModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Worker PINs Generated</h2>
              <p className="text-slate-600 mb-4">
                The following workers have been created with their PINs. Please download and securely share these credentials.
              </p>
              
              <div className="bg-slate-50 rounded-lg p-4 mb-6 max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2">Name</th>
                      <th className="text-left py-2">Employee ID</th>
                      <th className="text-left py-2">PIN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedWorkers.map((worker, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="py-2">{worker.name}</td>
                        <td className="py-2">{worker.employee_id}</td>
                        <td className="py-2 font-mono font-bold">{worker.pin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setShowPinsModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  onClick={downloadPins}
                  className="flex-1 bg-[#0a2240] hover:bg-[#0f325c] text-white"
                >
                  Download PINs CSV
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
